import { useEffect, type ReactNode } from "react";

import { themes } from "@/styles/themes";
import { useThemeStore } from "@/store/theme-store";

/**
 * Applies the active theme to <html>:
 *  - Sets the "light" / "dark" class so Tailwind's dark: variants fire.
 *  - Writes every theme variable as an inline custom property so the
 *    runtime store stays the source of truth (overrides theme.css if
 *    the two ever drift).
 *  - Subscribes to OS-preference changes when the user's preference is
 *    "system".
 *  - Defers enabling color transitions by one tick so the very first
 *    paint doesn't animate from a default.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((s) => s.theme);
  const preference = useThemeStore((s) => s.preference);
  const syncFromSystem = useThemeStore((s) => s.syncFromSystem);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    // Clear every theme variable from the inline style before applying
    // the next set. Without this, vars present in the *previous* theme
    // map but absent from the *current* one keep their stale inline
    // values and outrank the CSS cascade — e.g. switching light→dark
    // would leave light tokens leaking through wherever the dark map
    // doesn't define an override.
    const allKeys = new Set<string>([
      ...Object.keys(themes.light),
      ...Object.keys(themes.dark),
    ]);
    for (const key of allKeys) {
      root.style.removeProperty(key);
    }
    const vars = themes[theme];
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
  }, [theme]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      document.documentElement.classList.add("theme-transitions");
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => syncFromSystem();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference, syncFromSystem]);

  return <>{children}</>;
}
